import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Badge, 
  ListGroup, 
  Button, 
  Accordion,
  Spinner,
  Alert,
  Form,
  InputGroup
} from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';

const ProfileTab = () => {
  const { user } = useAuth();
  const [activeKey, setActiveKey] = useState('1');

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'moderator': return 'warning';
      default: return 'secondary';
    }
  };

  const getInitials = () => {
    const firstName = user?.telegram_first_name || '';
    const lastName = user?.telegram_last_name || '';
    
    if (firstName || lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    
    if (user?.name) {
      return user.name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    
    return 'U';
  };

  const getFullName = () => {
    const firstName = user?.telegram_first_name || '';
    const lastName = user?.telegram_last_name || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    
    return user?.name || 'Пользователь';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openTelegramProfile = () => {
    if (user?.telegram_username) {
      window.open(`https://t.me/${user.telegram_username}`, '_blank');
    }
  };

  if (!user) {
    return (
      <Row className="g-4">
        <Col>
          <Card className="text-center">
            <Card.Body>
              <div className="py-5">
                <i className="bi bi-exclamation-triangle display-1 text-warning"></i>
                <h4 className="mt-3">Данные пользователя не загружены</h4>
                <p className="text-muted">Пожалуйста, подождите...</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  const hasTelegramData = user.telegram_id || user.telegram_username;
  const emailVerified = user.email_verified || user.email_verified_at;

  return (
    <Container fluid className="py-4">
      <Row className="g-4">
        {/* Левая колонка - Профиль пользователя */}
        <Col lg={4}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white position-relative">
              <h5 className="mb-0">
                <i className="bi bi-person-badge me-2"></i>
                Профиль пользователя
              </h5>
              {user.is_superuser && (
                <Badge bg="warning" className="position-absolute top-0 end-0 mt-2 me-2">
                  <i className="bi bi-star-fill me-1"></i>
                  SUPERUSER
                </Badge>
              )}
            </Card.Header>
            <Card.Body className="text-center">
              {/* Аватар */}
              <div className="mb-4">
                {user.telegram_photo_url ? (
                  <img 
                    src={user.telegram_photo_url} 
                    alt="Avatar"
                    className="rounded-circle mb-3 border"
                    style={{ 
                      width: '100px', 
                      height: '100px',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 text-white fw-bold"
                    style={{ 
                      width: '100px', 
                      height: '100px',
                      fontSize: '2.5rem',
                      background: 'linear-gradient(135deg, #8dc0fb 0%, #6ba0d9 100%)'
                    }}
                  >
                    {getInitials()}
                  </div>
                )}
                
                <h4 className="mb-1">{getFullName()}</h4>
                
                {/* Telegram username */}
                {user.telegram_username && (
                  <div className="mb-2">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="rounded-pill"
                      onClick={openTelegramProfile}
                    >
                      <i className="bi bi-telegram me-1"></i>
                      @{user.telegram_username}
                    </Button>
                  </div>
                )}
                
                <Badge 
                  bg={getRoleBadgeVariant(user.role)} 
                  className="fs-6 px-3 py-2"
                  style={{ borderRadius: '20px' }}
                >
                  <i className="bi bi-shield-check me-1"></i>
                  {user.role === 'admin' ? 'Администратор' : 
                   user.role === 'moderator' ? 'Модератор' : 'Пользователь'}
                </Badge>
              </div>

              {/* Статус */}
              <div className="d-flex justify-content-center align-items-center mb-4">
                <div className="me-3">
                  <div 
                    className={`rounded-circle ${user.is_active ? 'bg-success' : 'bg-secondary'}`} 
                    style={{ width: '12px', height: '12px' }}
                  ></div>
                </div>
                <span className={user.is_active ? 'text-success fw-bold' : 'text-secondary'}>
                  {user.is_active ? 'Активен' : 'Неактивен'}
                </span>
              </div>
            </Card.Body>
          </Card>

          {/* Детальная информация с аккордеоном */}
          <Card className="mt-4 shadow-sm">
            <Accordion activeKey={activeKey} onSelect={(key) => setActiveKey(key)}>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <h6 className="mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    Детальная информация
                  </h6>
                </Accordion.Header>
                <Accordion.Body className="p-0">
                  <ListGroup variant="flush">
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Полное имя:</span>
                      <Badge bg="light" text="dark" className="font-monospace">
                        {getFullName()}
                      </Badge>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">ID пользователя:</span>
                      <Badge bg="light" text="dark" className="font-monospace">
                        #{user.id}
                      </Badge>
                    </ListGroup.Item>
                    
                    {user.telegram_id && (
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Telegram ID:</span>
                        <Badge bg="light" text="dark" className="font-monospace">
                          {user.telegram_id}
                        </Badge>
                      </ListGroup.Item>
                    )}

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Email:</span>
                      <div className="d-flex align-items-center">
                        <Badge bg="light" text="dark" className="font-monospace me-2">
                          {user.email || 'Не указан'}
                        </Badge>
                        {user.email && emailVerified && (
                          <Badge bg="success" className="fs-7">
                            <i className="bi bi-check-lg me-1"></i>
                            Подтвержден
                          </Badge>
                        )}
                      </div>
                    </ListGroup.Item>

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Статус аккаунта:</span>
                      <Badge bg={user.is_active ? 'success' : 'secondary'}>
                        {user.is_active ? 'Активен' : 'Заблокирован'}
                      </Badge>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Права:</span>
                      <div className="d-flex align-items-center">
                        <Badge bg={getRoleBadgeVariant(user.role)} className="fs-7 me-2">
                          {user.role === 'admin' && <i className="bi bi-star-fill me-1"></i>}
                          {user.role === 'moderator' && <i className="bi bi-shield me-1"></i>}
                          {user.role === 'user' && <i className="bi bi-person me-1"></i>}
                          {user.role === 'admin' ? 'Администратор' : 
                            user.role === 'moderator' ? 'Модератор' : 'Пользователь'}
                        </Badge>
                        {user.is_superuser && (
                          <Badge bg="warning" className="fs-7">
                            <i className="bi bi-star-fill me-1"></i>
                            Суперпользователь
                          </Badge>
                        )}
                      </div>
                    </ListGroup.Item>
                    
                    {hasTelegramData && (
                      <ListGroup.Item>
                        <div className="d-flex align-items-center text-success mb-1">
                          <i className="bi bi-telegram me-2"></i>
                          <small className="fw-bold">Привязан Telegram</small>
                        </div>
                        <small className="text-muted">Аккаунт подключен через Telegram</small>
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Card>
        </Col>

        {/* Правая колонка - Активность и статистика */}
        <Col lg={8}>
          <Card className="shadow-sm h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-activity me-2"></i>
                Активность и статистика
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center py-5">
                <i className="bi bi-graph-up-arrow display-1 text-muted opacity-50 mb-3"></i>
                <h4>Статистика активности</h4>
                <p className="text-muted">
                  Информация о вашей активности в системе будет отображаться здесь
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfileTab;